import { Contract } from "@/types/contract";
import { Flow } from "../types";
import { SCREENS } from "../screens";
import { isCustomerIdentified, showAttachmentsScreen, showS3AttachmentsScreen, skipSupplyPoint } from "../helpers";
import { isS3PreviousCustomer, shouldCollectGDPRAgreements } from "@/utils/contract";

export const S3_SELF_SERVICE: Flow = {
  [SCREENS.WELCOME]: {
    next: (contract: Contract) =>
      isS3PreviousCustomer(contract)
        ? SCREENS.SUPPLY_POINT_CONFIRM
        : showAttachmentsScreen(contract)
        ? SCREENS.ATTACHMENTS
        : isCustomerIdentified(contract)
        ? SCREENS.CUSTOMER
        : SCREENS.IDENTIFICATION,
    back: () => null,
  },
  [SCREENS.IDENTIFICATION]: {
    next: () => SCREENS.CUSTOMER,
    back: () => SCREENS.WELCOME,
  },
  [SCREENS.CUSTOMER]: {
    next: () => SCREENS.SUPPLY_POINT,
    back: () => SCREENS.WELCOME,
  },
  [SCREENS.SUPPLY_POINT_CONFIRM]: {
    next: () => SCREENS.PREVIOUS_CUSTOMER,
    back: () => SCREENS.WELCOME,
  },
  [SCREENS.PREVIOUS_CUSTOMER]: {
    next: (contract: Contract) =>
      skipSupplyPoint(contract)
        ? showS3AttachmentsScreen(contract)
          ? SCREENS.ATTACHMENTS
          : SCREENS.RECAPITULATION
        : SCREENS.SUPPLY_POINT,
    back: () => SCREENS.SUPPLY_POINT_CONFIRM,
  },
  [SCREENS.SUPPLY_POINT]: {
    next: (contract: Contract) =>
      isS3PreviousCustomer(contract) && showS3AttachmentsScreen(contract)
        ? SCREENS.ATTACHMENTS
        : !isS3PreviousCustomer(contract)
        ? SCREENS.SUPPLIER
        : SCREENS.RECAPITULATION,
    back: (contract: Contract) => (isS3PreviousCustomer(contract) ? SCREENS.PREVIOUS_CUSTOMER : SCREENS.CUSTOMER),
  },
  [SCREENS.SUPPLIER]: {
    next: (contract: Contract) => SCREENS.PAYMENTS,
    back: (contract: Contract) => SCREENS.SUPPLY_POINT,
  },
  [SCREENS.PAYMENTS]: {
    next: () => SCREENS.RECAPITULATION,
    back: (contract: Contract) => SCREENS.SUPPLIER,
  },
  [SCREENS.ATTACHMENTS]: {
    next: () => SCREENS.RECAPITULATION,
    back: (contract: Contract) => (skipSupplyPoint(contract) ? SCREENS.PREVIOUS_CUSTOMER : SCREENS.SUPPLY_POINT),
  },
  [SCREENS.RECAPITULATION]: {
    next: (contract: Contract, options?: { gdprAgreed?: boolean; isContractRequest?: boolean }) =>
      isS3PreviousCustomer(contract)
        ? SCREENS.VIEW
        : !options?.gdprAgreed && shouldCollectGDPRAgreements(contract, options?.isContractRequest ?? false)
        ? SCREENS.AGREEMENTS
        : SCREENS.VIEW,
    back: (contract: Contract) =>
      (isS3PreviousCustomer(contract) && showS3AttachmentsScreen(contract)) || showAttachmentsScreen(contract)
        ? SCREENS.ATTACHMENTS
        : !isS3PreviousCustomer(contract)
        ? SCREENS.PAYMENTS
        : skipSupplyPoint(contract)
        ? SCREENS.PREVIOUS_CUSTOMER
        : SCREENS.SUPPLY_POINT,
  },
  [SCREENS.AGREEMENTS]: {
    next: () => SCREENS.VIEW,
    back: () => SCREENS.RECAPITULATION,
  },
  [SCREENS.VIEW]: {
    next: () => SCREENS.SIGNATURE,
    back: () => SCREENS.RECAPITULATION,
  },
  [SCREENS.SIGNATURE]: {
    next: () => SCREENS.SMS_CODE,
    back: () => SCREENS.VIEW,
  },
  [SCREENS.SIGNATURE_ON_PHONE]: {
    next: () => SCREENS.THANK_YOU,
    back: () => null,
  },
  [SCREENS.SMS_CODE]: {
    next: () => SCREENS.THANK_YOU,
    back: () => SCREENS.SIGNATURE,
  },
  [SCREENS.THANK_YOU]: {
    next: () => null,
    back: () => null,
  },
};

export const S3_NO_SELF_SERVICE: Flow = {
  [SCREENS.WELCOME]: {
    next: (contract: Contract) =>
      isS3PreviousCustomer(contract)
        ? SCREENS.SUPPLY_POINT_CONFIRM
        : showAttachmentsScreen(contract)
        ? SCREENS.ATTACHMENTS
        : SCREENS.RECAPITULATION,
    back: () => null,
  },
  [SCREENS.SUPPLY_POINT_CONFIRM]: {
    next: () => SCREENS.PREVIOUS_CUSTOMER,
    back: () => SCREENS.WELCOME,
  },
  [SCREENS.PREVIOUS_CUSTOMER]: {
    next: (contract: Contract) =>
      skipSupplyPoint(contract)
        ? showS3AttachmentsScreen(contract)
          ? SCREENS.ATTACHMENTS
          : SCREENS.RECAPITULATION
        : SCREENS.SUPPLY_POINT,
    back: () => SCREENS.SUPPLY_POINT_CONFIRM,
  },
  [SCREENS.SUPPLY_POINT]: {
    next: (contract: Contract) => (showS3AttachmentsScreen(contract) ? SCREENS.ATTACHMENTS : SCREENS.RECAPITULATION),
    back: (contract: Contract) => (isS3PreviousCustomer(contract) ? SCREENS.PREVIOUS_CUSTOMER : SCREENS.RECAPITULATION),
  },
  [SCREENS.PAYMENTS]: {
    next: () => SCREENS.RECAPITULATION,
    back: () => SCREENS.RECAPITULATION,
  },
  [SCREENS.ATTACHMENTS]: {
    next: () => SCREENS.RECAPITULATION,
    back: (contract: Contract) =>
      isS3PreviousCustomer(contract)
        ? skipSupplyPoint(contract)
          ? SCREENS.PREVIOUS_CUSTOMER
          : SCREENS.SUPPLY_POINT
        : SCREENS.WELCOME,
  },
  [SCREENS.RECAPITULATION]: {
    next: (contract: Contract, options?: { gdprAgreed?: boolean; isContractRequest?: boolean }) =>
      isS3PreviousCustomer(contract)
        ? SCREENS.VIEW
        : !options?.gdprAgreed && shouldCollectGDPRAgreements(contract, options?.isContractRequest ?? false)
        ? SCREENS.AGREEMENTS
        : SCREENS.VIEW,
    back: (contract: Contract) =>
      (!isS3PreviousCustomer(contract) && showAttachmentsScreen(contract)) ||
      (isS3PreviousCustomer(contract) && showS3AttachmentsScreen(contract))
        ? SCREENS.ATTACHMENTS
        : !isS3PreviousCustomer(contract)
        ? SCREENS.WELCOME
        : skipSupplyPoint(contract)
        ? SCREENS.PREVIOUS_CUSTOMER
        : SCREENS.SUPPLY_POINT,
  },
  [SCREENS.AGREEMENTS]: {
    next: () => SCREENS.VIEW,
    back: () => SCREENS.RECAPITULATION,
  },
  [SCREENS.VIEW]: {
    next: () => SCREENS.SIGNATURE,
    back: () => SCREENS.RECAPITULATION,
  },
  [SCREENS.SIGNATURE]: {
    next: () => SCREENS.SMS_CODE,
    back: () => SCREENS.VIEW,
  },
  [SCREENS.SIGNATURE_ON_PHONE]: {
    next: () => SCREENS.THANK_YOU,
    back: () => null,
  },
  [SCREENS.SMS_CODE]: {
    next: () => SCREENS.THANK_YOU,
    back: () => SCREENS.SIGNATURE,
  },
  [SCREENS.THANK_YOU]: {
    next: () => null,
    back: () => null,
  },
};
